import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { DateTime } from "luxon";

import LoadingWheel from "../../components/shared/LoadingWheel";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import { useAuth } from "../../contexts/AuthContext";
import useVehicleSingle from "../../data/useVehicleSingle";
import useVehiclesMutations from "../../data/useVehiclesMutations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { TextInput } from "../../components/shared/Inputs/TextInput";
import { NumberInput } from "../../components/shared/Inputs/NumberInput";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import { CheckboxInput } from "../../components/shared/Inputs/CheckboxInput";
import Card from "../../components/shared/Card/Card";
import KTypeLookupModal from "../../components/shared/KTypeLookupModal";
import { IVehicle } from "../../models/Vehicle";
import UkvdLookup from "./UkvdLookup";

export default function EditVehicle() {
  const { setInfo } = usePageNameContext();
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const { vehicle } = useVehicleSingle(vehicleId);
  const { updateDetails } = useVehiclesMutations();
  const { saveHandlers } = useDefaultCRUDHandlers("Vehicle");
  const { claims } = useAuth();
  const [kTypeLookupOpen, setKTypeLookupOpen] = useState(false);

  // Helper function to convert ISO date string to YYYY-MM-DD format for date inputs
  const formatDateForInput = (dateString: string | null | undefined): string | null => {
    if (!dateString) return null;
    try {
      const date = DateTime.fromISO(dateString);
      if (!date.isValid) return null;
      return date.toISODate() || null;
    } catch {
      return null;
    }
  };

  // Transform vehicle data to format dates for HTML date inputs
  const transformVehicleData = (vehicleData: IVehicle) => {
    if (!vehicleData) return {};
    
    return {
      ...vehicleData,
      motExpiryDate: formatDateForInput(vehicleData.motExpiryDate),
      dateFirstRegistered: formatDateForInput(vehicleData.dateFirstRegistered),
      dateOfFirstRegistrationUK: formatDateForInput(vehicleData.dateOfFirstRegistrationUK),
      visibilityDate: formatDateForInput(vehicleData.visibilityDate),
      terminateDate: formatDateForInput(vehicleData.terminateDate),
    };
  };

  const { register, handleSubmit, reset, formState: { errors }, setValue } = useForm<Partial<IVehicle>>({
    defaultValues: vehicle.data?.data ? transformVehicleData(vehicle.data.data) : {},
  });

  // Reset form when vehicle data loads
  useEffect(() => {
    if (vehicle.data?.data) {
      reset(transformVehicleData(vehicle.data.data));
    }
  }, [vehicle.data?.data, reset]);

  useEffect(() => {
    if (!vehicle.data?.data && !vehicle.isLoading) {
      setInfo({
        name: "Vehicle not found",
        desc: "The vehicle you are looking for does not exist",
      });
      return;
    }

    if (vehicle.data?.data) {
      setInfo({
        name: `Edit ${vehicle.data.data.vrm} ${vehicle.data.data.combinedMake} ${vehicle.data.data.modelRangeDescription || vehicle.data.data.combinedModel}`,
        desc: `Edit vehicle details`,
      });
    }
  }, [vehicleId, vehicle.isLoading, vehicle.data?.data]);

  const onSubmit = (data: Partial<IVehicle>) => {
    if (!vehicle.data?.data || !vehicleId) return;

    updateDetails.mutate(
      {
        vehicleId,
        details: data,
      },
      {
        onSuccess: () => {
          saveHandlers.onSuccess();
          navigate(`/vehicles/${vehicleId}`);
        },
        onError: () => {
          saveHandlers.onError();
        },
      }
    );
  };

  if (vehicle.isLoading || !vehicle.data?.data) {
    return (
      <div className="mt-8 min-h-[50vh] my-4 p-4 bg-white rounded-lg shadow">
        <LoadingWheel />
      </div>
    );
  }

  return (
    <div className="mt-8">
      <Card title="Edit Vehicle Details" bodyClassName="p-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">

          <div>
            <p className="text-red-600 font-bold">WARNING: Manually editing a vehicle could lead to incorrect details being passed onto parts and marketplaces!</p>
          </div>


          {/* Basic Information Section */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <TextInput
                label="VRM"
                register={register}
                registerName="vrm"
                disabled={true}
                cols="sm:col-span-1"
              />
              <UkvdLookup displayName="Make" registerName="combinedMake" setValue={setValue} vehiclePackageData={vehicle.data.data.vehiclePackageData}>
                <TextInput
                  label="Make"
                  register={register}
                  registerName="combinedMake"
                  placeholder="FORD"
                  cols="sm:col-span-1 grow"
                />
              </UkvdLookup>
              
              <UkvdLookup displayName="Model" registerName="combinedModel" setValue={setValue} vehiclePackageData={vehicle.data.data.vehiclePackageData}>
                <TextInput
                  label="Model"
                  register={register}
                  registerName="combinedModel"
                  placeholder="FOCUS EDGE TDCI 95"
                  cols="sm:col-span-1 grow"
                />
              </UkvdLookup>
              <TextInput
                label="Model Range"
                register={register}
                registerName="modelRangeDescription"
                placeholder="Focus"
                cols="sm:col-span-1"
              />
              <NumberInput
                label="Year of Manufacture"
                register={register}
                registerName="dvlaYearOfManufacture"
                placeholder="2014"
                classes="sm:col-span-1"
              />
              <TextInput
                label="Chassis No (VIN)"
                register={register}
                registerName="combinedVIN"
                placeholder=""
                cols="sm:col-span-1"
              />
            </div>
          </div>

          {/* Engine Information Section */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Engine Information</h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <NumberInput
                label="Engine Size (cc)"
                register={register}
                registerName="combinedEngineCapacity"
                placeholder="1560"
                classes="sm:col-span-1"
              />
              <TextInput
                label="Engine Code"
                register={register}
                registerName="engineModelCode"
                placeholder=""
                cols="sm:col-span-1"
              />
              <TextInput
                label="Engine Manufacturer"
                register={register}
                registerName="engineManufacturer"
                placeholder="Ford"
                cols="sm:col-span-1"
              />
              <NumberInput
                label="Number of Cylinders"
                register={register}
                registerName="numberOfCylinders"
                placeholder="4"
                classes="sm:col-span-1"
              />
              <TextInput
                label="Engine Location"
                register={register}
                registerName="engineLocation"
                placeholder="Front"
                cols="sm:col-span-1"
              />
              <TextInput
                label="Aspiration"
                register={register}
                registerName="aspiration"
                placeholder="TURBOCHARGED"
                cols="sm:col-span-1"
              />
            </div>
          </div>

          {/* Body & Style Section */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Body & Style</h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <TextInput
                label="Body Style"
                register={register}
                registerName="bodyStyleDescription"
                placeholder="Estate"
                cols="sm:col-span-1"
              />
              <NumberInput
                label="Number of Doors"
                register={register}
                registerName="numberOfDoors"
                placeholder="5"
                classes="sm:col-span-1"
              />
              <NumberInput
                label="Seats"
                register={register}
                registerName="seats"
                placeholder="5"
                classes="sm:col-span-1"
              />
              <TextInput
                label="Vehicle Category"
                register={register}
                registerName="vehicleCategoryDescription"
                placeholder="Car"
                cols="sm:col-span-1"
              />
            </div>
          </div>

          {/* Fuel & Transmission Section */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Fuel & Transmission</h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <TextInput
                label="Fuel Type"
                register={register}
                registerName="combinedFuelType"
                placeholder="Diesel"
                cols="sm:col-span-1"
              />
              <TextInput
                label="Fuel Description"
                register={register}
                registerName="fuelDescription"
                placeholder="HEAVY OIL"
                cols="sm:col-span-1"
              />
              <TextInput
                label="Transmission"
                register={register}
                registerName="combinedTransmission"
                placeholder="Manual"
                cols="sm:col-span-1"
              />
              <NumberInput
                label="Forward Gears"
                register={register}
                registerName="combinedForwardGears"
                placeholder="6"
                classes="sm:col-span-1"
              />
              <TextInput
                label="Transmission Code"
                register={register}
                registerName="vinvisTransmissionCode"
                placeholder=""
                cols="sm:col-span-1"
              />
              <TextInput
                label="Drive Type"
                register={register}
                registerName="driveType"
                placeholder="4x2"
                cols="sm:col-span-1"
              />
            </div>
          </div>

          {/* Colour Section */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Colour</h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <TextInput
                label="Current Colour"
                register={register}
                registerName="colourCurrent"
                placeholder="SILVER"
                cols="sm:col-span-1"
              />
              <TextInput
                label="Original Colour"
                register={register}
                registerName="colourOriginal"
                placeholder="SILVER"
                cols="sm:col-span-1"
              />
              <TextInput
                label="Colour Code"
                register={register}
                registerName="vinvisColourCode"
                placeholder="BNXEWWA"
                cols="sm:col-span-1"
              />
            </div>
          </div>

          {/* Technical Specifications Section */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Technical Specifications</h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <NumberInput
                label="Gross Weight (KG)"
                register={register}
                registerName="grossWeightKG"
                placeholder="1900"
                classes="sm:col-span-1"
              />
              <NumberInput
                label="Kerb Weight Min (KG)"
                register={register}
                registerName="kerbWeightMin"
                placeholder="1400"
                classes="sm:col-span-1"
              />
              <NumberInput
                label="Kerb Weight Max (KG)"
                register={register}
                registerName="kerbWeightMax"
                placeholder="1400"
                classes="sm:col-span-1"
              />
              <NumberInput
                label="Height (MM)"
                register={register}
                registerName="heightMM"
                placeholder="1500"
                classes="sm:col-span-1"
              />
              <NumberInput
                label="Length (MM)"
                register={register}
                registerName="lengthMM"
                placeholder="4500"
                classes="sm:col-span-1"
              />
              <NumberInput
                label="Width (MM)"
                register={register}
                registerName="widthMM"
                placeholder="2000"
                classes="sm:col-span-1"
              />
            </div>
          </div>

          {/* MOT & Mileage Section */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">MOT & Mileage</h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <NumberInput
                label="Mileage (Miles)"
                register={register}
                registerName="odometerInMiles"
                placeholder="135488"
                classes="sm:col-span-1"
              />
              <TextInput
                label="MOT Expiry Date"
                register={register}
                registerName="motExpiryDate"
                type="date"
                cols="sm:col-span-1"
              />
            </div>
          </div>

          {/* Dates Section */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Dates</h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <TextInput
                label="Date First Registered"
                register={register}
                registerName="dateFirstRegistered"
                type="date"
                cols="sm:col-span-1"
              />
              <TextInput
                label="Date of First Registration UK"
                register={register}
                registerName="dateOfFirstRegistrationUK"
                type="date"
                cols="sm:col-span-1"
              />
              <TextInput
                label="Visibility Date"
                register={register}
                registerName="visibilityDate"
                type="date"
                cols="sm:col-span-1"
              />
              <TextInput
                label="Terminate Date"
                register={register}
                registerName="terminateDate"
                type="date"
                cols="sm:col-span-1"
              />
            </div>
          </div>

          {/* Additional Codes Section */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Codes</h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <div className="sm:col-span-1">
                <div className="flex items-end gap-2">
                  <NumberInput
                    label="K Type"
                    register={register}
                    registerName="kType"
                    placeholder="8170"
                    classes="min-w-0 flex-1"
                  />
                  <ButtonInput
                    label="Lookup"
                    isSubmit={false}
                    onClick={() => setKTypeLookupOpen(true)}
                    classes="!bg-white !text-gray-700 border !border-gray-300 hover:!bg-gray-50 shrink-0 self-end"
                  />
                </div>
                <KTypeLookupModal
                  open={kTypeLookupOpen}
                  onClose={() => setKTypeLookupOpen(false)}
                  initialMake={vehicle.data?.data?.combinedMake || vehicle.data?.data?.dvlaMake}
                  initialModel={vehicle.data?.data?.combinedModel || vehicle.data?.data?.dvlaModel}
                  vehicle={vehicle.data?.data}
                  onSelect={(kType) => {
                    setValue("kType", kType, { shouldDirty: true });
                    setKTypeLookupOpen(false);
                  }}
                />
              </div>
              <TextInput
                label="Euro Status"
                register={register}
                registerName="euroStatus"
                placeholder="5b"
                cols="sm:col-span-1"
              />
              <TextInput
                label="Country of Origin"
                register={register}
                registerName="countryOfOrigin"
                placeholder="Germany"
                cols="sm:col-span-1"
              />
            </div>
          </div>

          {/* Purchase VAT Section */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Purchase VAT</h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <CheckboxInput
                label="VAT charged on purchase (trade seller)"
                register={register}
                registerName="applyVAT"
                disabled={!claims?.isAdmin}
              />
            </div>
            <p className="mt-2 text-xs text-gray-500">
              {claims?.isAdmin
                ? "Changing this updates any linked leads and, when the margin scheme is enabled, re-stamps the vehicle's sales VAT treatment and margin pool entry."
                : "Only admins can change the purchase VAT flag."}
            </p>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-4 pt-4 border-t">
            <ButtonInput
              label="Cancel"
              isSubmit={false}
              onClick={() => navigate(`/vehicles/${vehicleId}`)}
              classes="px-6"
            />
            <ButtonInput
              label="Save Changes"
              isSubmit={true}
              classes="px-6"
              onClick={() => {}}
            />
          </div>
        </form>
      </Card>
    </div>
  );
}

